import React, { useRef, useState, useEffect, useCallback, useId } from 'react';

const DETECT_INTERVAL = 300;

function createDetector() {
  if (typeof window !== 'undefined' && window.FaceDetector) {
    return new window.FaceDetector({ fastMode: true, maxDetectedFaces: 1 });
  }
  return null;
}

export default function FaceCapture({ onCapture, onCancel }) {
  const maskId = `fc-${useId().replace(/:/g, '')}`;
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const detectorRef = useRef(null);
  const tickRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [status, setStatus] = useState('none');
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    detectorRef.current = createDetector();

    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
          audio: false,
        });
        if (!mounted) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setReady(true);
      } catch {
        if (mounted) setError('Không thể truy cập camera. Vui lòng cấp quyền truy cập.');
      }
    })();

    return () => {
      mounted = false;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, []);

  useEffect(() => {
    if (!ready || !detectorRef.current) return;

    tickRef.current = setInterval(async () => {
      const video = videoRef.current;
      if (!video || video.readyState < 4) return;
      try {
        const faces = await detectorRef.current.detect(video);
        if (faces.length === 0) { setStatus('none'); return; }
        const { x, y, width, height } = faces[0].boundingBox;
        const vw = video.videoWidth;
        const vh = video.videoHeight;
        const dx = Math.abs((x + width / 2) / vw - 0.5);
        const dy = Math.abs((y + height / 2) / vh - 0.45);
        setStatus(dx < 0.15 && dy < 0.2 ? 'aligned' : 'detected');
      } catch { /* ignore */ }
    }, DETECT_INTERVAL);

    return () => clearInterval(tickRef.current);
  }, [ready]);

  const handleCapture = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        streamRef.current?.getTracks().forEach((t) => t.stop());
        onCapture(new File([blob], 'selfie.jpg', { type: 'image/jpeg' }));
      },
      'image/jpeg',
      0.92
    );
  }, [onCapture]);

  if (error) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-xl bg-gray-900 p-8">
        <p className="text-sm text-red-400">{error}</p>
        <button type="button" onClick={onCancel} className="text-sm text-blue-400 underline">
          Quay lại
        </button>
      </div>
    );
  }

  const borderColor =
    status === 'aligned' ? '#22c55e' : status === 'detected' ? '#eab308' : '#6b7280';

  return (
    <div className="overflow-hidden rounded-xl bg-black">
      <div className="relative">
        <video
          ref={videoRef}
          className="block w-full"
          muted
          playsInline
          style={{ transform: 'scaleX(-1)' }}
        />
        {ready && (
          <svg className="pointer-events-none absolute inset-0 h-full w-full">
            <defs>
              <mask id={maskId}>
                <rect width="100%" height="100%" fill="white" />
                <ellipse cx="50%" cy="45%" rx="28%" ry="38%" fill="black" />
              </mask>
            </defs>
            <rect width="100%" height="100%" fill="rgba(0,0,0,0.6)" mask={`url(#${maskId})`} />
            <ellipse
              cx="50%" cy="45%" rx="28%" ry="38%"
              fill="none"
              stroke={borderColor}
              strokeWidth="3"
              className="transition-all duration-300"
            />
          </svg>
        )}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-4 pb-4 pt-10 text-center">
          <p className="text-sm font-medium text-white">
            {!ready && 'Đang khởi tạo camera...'}
            {ready && status === 'aligned' && 'Khuôn mặt đã khớp — Nhấn chụp'}
            {ready && status !== 'aligned' && 'Đưa khuôn mặt vào vùng khung hình'}
          </p>
        </div>
      </div>

      <canvas ref={canvasRef} className="hidden" />

      <div className="flex items-center justify-center gap-6 bg-gray-900 py-4">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-full bg-gray-700 px-5 py-2.5 text-sm font-medium text-white hover:bg-gray-600"
        >
          Hủy
        </button>
        <button
          type="button"
          onClick={handleCapture}
          disabled={!ready}
          className="flex h-16 w-16 items-center justify-center rounded-full border-4 border-white transition-opacity disabled:opacity-40"
        >
          <div
            className={`h-12 w-12 rounded-full transition-colors duration-300 ${
              status === 'aligned' ? 'bg-green-400' : 'bg-white'
            }`}
          />
        </button>
        <div className="w-[84px]" />
      </div>
    </div>
  );
}
